import React, { useRef, useState } from 'react';
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ArrowsRightLeftIcon,
  ArrowUpTrayIcon,
  DocumentTextIcon,
  PhotoIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { docConvertAPI } from '../../api/docConvertApi';

const COLUMNS = [
  { key: 'sira_no', label: 'Sıra No' },
  { key: 'ilgili_not_adi', label: 'İlgili Not Adı' },
  { key: 'not_tarihi', label: 'Not Tarihi' },
  { key: 'not_basligi', label: 'Not Başlığı' },
  { key: 'not', label: 'Not' },
  { key: 'donusler', label: 'Dönüşler' },
  { key: 'gorsel_sayisi', label: 'Not Görseli' },
  { key: 'donus_gorsel_sayisi', label: 'Dönüş Görseli' },
  { key: 'ilgili_departman', label: 'İlgili Departmanlar' },
  { key: 'ilgili_kisi', label: 'İlgili Kişiler' },
  { key: 'durum', label: 'Durum' },
];

const WordToExcelPage = () => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [savingToDb, setSavingToDb] = useState(false);
  const inputRef = useRef(null);

  const selectFile = (selected) => {
    setError(null);
    setResult(null);
    if (!selected) return;
    if (!selected.name.toLowerCase().endsWith('.docx')) {
      setError('Sadece .docx uzantılı Word dosyaları desteklenir.');
      return;
    }
    setFile(selected);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    selectFile(e.dataTransfer.files?.[0]);
  };

  const handleConvert = async () => {
    if (!file || loading) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const response = await docConvertAPI.convert(file);
      if (response?.success) {
        setResult(response);
      } else {
        setError(response?.error || 'Dönüştürme başarısız oldu.');
      }
    } catch (err) {
      setError(err.message || 'Dönüştürme sırasında bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!result?.conversion_id || downloading) return;
    setDownloading(true);
    setError(null);
    try {
      await docConvertAPI.downloadExcel(result.conversion_id, result.filename);
    } catch (err) {
      setError(err.message || 'Excel indirilemedi.');
    } finally {
      setDownloading(false);
    }
  };

  const handleSaveToDb = async () => {
    if (!result?.conversion_id || savingToDb || result.saved_to_db) return;
    setSavingToDb(true);
    setError(null);
    try {
      const response = await docConvertAPI.importConversionToDb(result.conversion_id);
      if (response?.success) {
        setResult((prev) => ({
          ...prev,
          saved_to_db: true,
          saved_count: response.saved_count,
        }));
      } else {
        setError(response?.message || 'Notlar kaydedilemedi.');
      }
    } catch (err) {
      setError(err.message || 'Notlar kaydedilemedi.');
    } finally {
      setSavingToDb(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setResult(null);
    setError(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div className="p-4 md:p-8 max-w-5xl mx-auto space-y-6">
      {/* Hero başlık */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-orange-500 to-amber-600 p-6 md:p-8 text-white shadow-lg">
        <div className="absolute -right-8 -top-8 h-40 w-40 rounded-full bg-white/10" />
        <div className="absolute -right-2 bottom-0 opacity-20">
          <ArrowsRightLeftIcon className="h-28 w-28" />
        </div>
        <h1 className="relative text-2xl md:text-3xl font-bold">Word → Excel Dönüştürücü</h1>
        <p className="relative mt-2 max-w-xl text-sm text-white/90">
          Word dosyanızı yükleyin; notlar yapay zeka ile ayrıştırılır ve görselleriyle birlikte
          Excel tablosuna dönüştürülür. Metinler birebir korunur. Word&apos;de{' '}
          <span className="font-semibold text-red-200">kırmızı yazı</span> otomatik olarak Dönüşler sütununa alınır.
        </p>
      </div>

      {/* Yükleme alanı */}
      <div
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        className={`group cursor-pointer rounded-3xl border-2 border-dashed p-10 text-center transition-all ${
          isDragging
            ? 'border-orange-500 bg-orange-50 dark:bg-orange-950/30 scale-[1.01]'
            : 'border-gray-300 dark:border-gray-600 hover:border-orange-400 bg-white dark:bg-gray-800'
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".docx"
          className="hidden"
          onChange={(e) => selectFile(e.target.files?.[0])}
        />
        {file ? (
          <div className="flex items-center justify-center gap-3">
            <div className="grid place-items-center h-12 w-12 rounded-2xl bg-orange-100 dark:bg-orange-950/40 text-orange-600">
              <DocumentTextIcon className="h-7 w-7" />
            </div>
            <div className="text-left">
              <p className="font-medium text-gray-900 dark:text-gray-100">{file.name}</p>
              <p className="text-xs text-gray-500">{(file.size / 1024).toFixed(0)} KB</p>
            </div>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); handleReset(); }}
              className="ml-2 rounded-full p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700"
              title="Dosyayı kaldır"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2 text-gray-500 dark:text-gray-400">
            <div className="grid place-items-center h-14 w-14 rounded-2xl bg-orange-50 dark:bg-orange-950/30 text-orange-500 group-hover:scale-105 transition-transform">
              <ArrowUpTrayIcon className="h-7 w-7" />
            </div>
            <p className="font-medium text-gray-700 dark:text-gray-200">Word dosyasını buraya sürükleyin veya tıklayıp seçin</p>
            <p className="text-xs">.docx — en fazla 100 MB</p>
          </div>
        )}
      </div>

      {/* Aksiyonlar */}
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={handleConvert}
          disabled={!file || loading}
          className="inline-flex items-center gap-2 rounded-xl bg-orange-600 px-5 py-2.5 text-sm font-medium text-white shadow-md hover:bg-orange-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {loading ? (
            <>
              <ArrowPathIcon className="h-4 w-4 animate-spin" />
              Dönüştürülüyor… (uzun belgeler parça parça işlenir, birkaç dakika sürebilir)
            </>
          ) : (
            <>
              <ArrowPathIcon className="h-4 w-4" />
              Excel'e Dönüştür
            </>
          )}
        </button>

        {result?.success && !result.conversion_id && (
          <span className="text-sm text-amber-600 dark:text-amber-400">
            Dönüştürme tamamlandı ancak sunucu eski sürümde çalışıyor — indirme için
            sunucudaki backend güncellenmeli (git pull + yeniden başlatma).
          </span>
        )}

        {result?.conversion_id && (
          <>
            <button
              type="button"
              onClick={handleDownload}
              disabled={downloading}
              className="inline-flex items-center gap-2 rounded-xl bg-green-600 px-5 py-2.5 text-sm font-medium text-white shadow-md hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
              {downloading ? 'İndiriliyor…' : `Excel'i İndir (${result.row_count} satır)`}
            </button>
            <button
              type="button"
              onClick={handleSaveToDb}
              disabled={savingToDb || result.saved_to_db}
              className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-medium text-white shadow-md hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <DocumentTextIcon className="h-4 w-4" />
              {result.saved_to_db
                ? `Programa kaydedildi (${result.saved_count ?? result.row_count} not)`
                : savingToDb
                  ? 'Kaydediliyor…'
                  : 'Programa Kaydet'}
            </button>
          </>
        )}
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300">
          {error}
        </div>
      )}

      {result?.partial_coverage && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
          Belgenin bir kısmı notlara atanamadı ({result.coverage_percent ?? '?'}% kapsam).
          {result.blocks_unassigned != null && (
            <> {result.blocks_unassigned} blok atanmamış kaldı.</>
          )}
          {' '}Word dosyasında her notun ayrı paragraf/satır olduğundan emin olun veya dosyayı bölerek tekrar deneyin.
        </div>
      )}

      {result?.success && !result.partial_coverage && result.block_count != null && (
        <div className="rounded-xl border border-green-200 bg-green-50 p-4 text-sm text-green-800 dark:border-green-800 dark:bg-green-950/40 dark:text-green-200">
          {result.row_count} not satırı oluşturuldu — {result.blocks_assigned}/{result.block_count} blok atandı
          ({result.coverage_percent ?? 100}%).
          {result.chunks_processed > 1 && (
            <> {result.chunks_processed} parça halinde işlendi.</>
          )}
          {result.recovery_passes > 0 && ' Eksik bloklar için ek tur çalıştırıldı.'}
          {result.red_block_count > 0 && (
            <> {result.red_block_count} kırmızı blok dönüş olarak işlendi.</>
          )}
          {result.char_count > 0 && (
            <div className="mt-1 font-medium">
              {(result.char_converted ?? 0).toLocaleString('tr-TR')} /{' '}
              {result.char_count.toLocaleString('tr-TR')} karakter aktarıldı
              {' '}({result.char_coverage_percent ?? 100}%).
              {(result.char_coverage_percent ?? 100) >= 100
                ? ' Tüm metin eksiksiz aktarıldı.'
                : ' Bir kısım metin aktarılamadı — kaynak dosyayı kontrol edin.'}
            </div>
          )}
        </div>
      )}

      {/* Önizleme */}
      {result?.rows?.length > 0 && (
        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800 overflow-hidden">
          <div className="border-b border-gray-200 px-5 py-3.5 dark:border-gray-700">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Önizleme — {result.row_count} not ayrıştırıldı
              {result.block_count != null && (
                <span className="font-normal text-gray-500 dark:text-gray-400">
                  {' '}· {result.blocks_assigned}/{result.block_count} blok
                </span>
              )}
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Görseller Excel dosyasının içine gömülüdür; aşağıda yalnızca adetleri gösterilir.
            </p>
          </div>
          <div className="max-h-[60vh] overflow-auto">
            <table className="min-w-full text-xs">
              <thead className="sticky top-0 bg-[#F2CC8F] text-gray-900">
                <tr>
                  {COLUMNS.map((col) => (
                    <th key={col.key} className="whitespace-nowrap px-3 py-2 text-left font-semibold">
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {result.rows.map((row, idx) => (
                  <tr
                    key={idx}
                    className={`align-top border-b border-gray-100 dark:border-gray-700 ${
                      idx % 2 === 0 ? 'bg-[#FBF1DE]/60 dark:bg-gray-700/30' : 'bg-white dark:bg-gray-800'
                    }`}
                  >
                    {COLUMNS.map((col) => (
                      <td key={col.key} className="px-3 py-2 text-gray-800 dark:text-gray-200">
                        {col.key === 'gorsel_sayisi' ? (
                          row.gorsel_sayisi > 0 ? (
                            <span className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400">
                              <PhotoIcon className="h-4 w-4" />
                              {row.gorsel_sayisi} görsel
                            </span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )
                        ) : (
                          <span className={col.key === 'not' || col.key === 'donusler' ? 'block max-w-md whitespace-pre-wrap' : 'whitespace-nowrap'}>
                            {row[col.key] || <span className="text-gray-400">—</span>}
                          </span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default WordToExcelPage;
